import React, { useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import POAItem, { POAItemModel } from './POAItem';
import PilotBadge from './PilotBadge';
import MentionInput from './MentionInput';
import { dayHeaderLabel, dateForDay, DAY_LABELS } from '../utils/poa-helpers';
import { buttonClass, IconButton } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import { EmptyState } from '../components/ui/EmptyState';
import Reveal from '../components/ui/Reveal';
import { Appear } from '../components/ui/Appear';

export interface PilotEntry {
  user_id: string;
  firstname: string;
  event_id: string;
  event_name: string;
  /** Crew role on this event ("Pilot", "Producer", "Ground Crew", etc.); empty if unset. */
  role?: string;
}

export type DayColumnSize = 'compact' | 'focus' | 'agenda';

interface Props {
  weekStart: string;
  day: number;
  items: POAItemModel[];
  pilots: PilotEntry[];
  isManager: boolean;
  size: DayColumnSize;
  isToday?: boolean;
  /** Optional click-to-promote affordance — surfaces an icon button in the header. */
  onPromote?: () => void;
  onUpdate: (id: string, fields: { body?: string; is_completable?: boolean }) => Promise<void>;
  onComplete: (id: string, completed: boolean) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onCreate: (day: number, body: string, isCompletable: boolean) => Promise<void>;
}

/**
 * One Mon..Sun day column. Three layout variants:
 *
 *  - compact: standard 7-col grid card. Item bodies are line-clamp-2 by
 *    default and reveal in full on day-card hover (group-hover). Most
 *    constrained vertical space.
 *  - focus:   the "featured day" panel. Larger padding, bigger header,
 *    no truncation, more prominent pilot badges. Renders full-width.
 *  - agenda:  vertical-list day. Full-width but compact padding; no
 *    truncation since horizontal space is generous.
 *
 * Each column registers a useDroppable so cross-day drag works even when
 * the destination is empty (a SortableContext alone has no drop surface
 * for empty lists). Click-to-promote (when provided) surfaces a small
 * ↑ icon in the header that hoists this day into the focus panel.
 */
const DayColumn: React.FC<Props> = ({
  weekStart, day, items, pilots, isManager,
  size, isToday, onPromote,
  onUpdate, onComplete, onDelete, onCreate,
}) => {
  const [composing, setComposing] = useState(false);
  const [draft, setDraft] = useState('');
  const [draftCompletable, setDraftCompletable] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { setNodeRef, isOver } = useDroppable({
    id: `day-${day}`,
    data: { day, type: 'day' },
  });

  const itemIds = items.map(i => i.id);
  const truncated = size === 'compact';
  const isFocus = size === 'focus';

  const startCompose = () => { setDraft(''); setDraftCompletable(false); setComposing(true); };
  const cancelCompose = () => { setComposing(false); };
  const submitCompose = async () => {
    if (submitting || !draft.trim()) return;
    setSubmitting(true);
    try {
      await onCreate(day, draft, draftCompletable);
      setDraft('');
      setDraftCompletable(false);
      setComposing(false);
    } finally {
      setSubmitting(false);
    }
  };

  // --- Visual variants -----------------------------------------------------
  // The accent bar grows + heading switches from uppercase mini-caps to
  // sentence-case in focus mode. Compact + agenda share the mini-caps look.
  const sizeClasses = {
    outer: {
      // Compact cards stretch to row height (set by parent's min-h-[240px]).
      // Focus + agenda keep an explicit min-height since they aren't constrained.
      compact: 'h-full min-h-0',
      focus:   'min-h-[200px]',
      agenda:  'min-h-[80px]',
    }[size],
    headerPadding: {
      compact: 'px-3 py-2',
      focus:   'px-5 py-3.5',
      agenda:  'px-4 py-2.5',
    }[size],
    accentBar: {
      compact: 'w-1 h-3',
      focus:   'w-1.5 h-5',
      agenda:  'w-1 h-3.5',
    }[size],
    headingClass: {
      compact: 'text-[11px] uppercase tracking-widest font-semibold',
      focus:   'text-base font-semibold tracking-tight',
      agenda:  'text-[11px] uppercase tracking-widest font-semibold',
    }[size],
    listPadding: {
      compact: 'p-1.5 space-y-0.5',
      focus:   'p-3 space-y-1',
      agenda:  'p-2 space-y-0.5',
    }[size],
  };

  // Glow color shifts with state — primary (yellow) for today, sky for everyone else,
  // and a slightly stronger glow for the focus card so it reads as promoted.
  const boxShadow = isFocus
    ? (isToday
        ? '0 0 50px rgba(255,204,51,0.10), 0 1px 3px rgba(0,0,0,.5)'
        : '0 0 50px rgba(14,165,233,0.08), 0 1px 3px rgba(0,0,0,.5)')
    : (isToday
        ? '0 0 30px rgba(255,204,51,0.06), 0 1px 3px rgba(0,0,0,.4)'
        : '0 0 30px rgba(14,165,233,0.03), 0 1px 3px rgba(0,0,0,.4)');

  const headerLabel = isFocus
    ? `${DAY_LABELS[day]} · ${dayHeaderLabel(weekStart, day).split(' · ')[1]}`
    : dayHeaderLabel(weekStart, day);

  return (
    <div
      // The `group` class enables CSS group-hover on POAItem bodies so
      // line-clamp expands to full when the user hovers anywhere on this card.
      className={`group flex flex-col bg-surface-container-low border rounded-md overflow-hidden
                  transition-all duration-200
                  ${isOver ? 'border-primary/50 ring-1 ring-primary/30' : 'border-transparent'}
                  ${sizeClasses.outer}`}
      style={{ boxShadow }}
    >
      {/* Day header */}
      <div className={`border-b border-outline-variant bg-gray-900/40 ${sizeClasses.headerPadding}`}>
        <div className="flex items-center gap-2.5">
          <div className={`${sizeClasses.accentBar} rounded-full ${isToday ? 'bg-primary/80' : 'bg-sky-500/50'}`} />
          <h3 className={`${sizeClasses.headingClass} ${isToday ? 'text-primary' : 'text-on-surface'}`}>
            {headerLabel}
          </h3>

          {isFocus && (
            <span className="ml-2 text-[10px] uppercase tracking-wider text-on-surface-variant font-medium select-none">
              {isToday ? 'today' : 'featured'}
            </span>
          )}

          {!isFocus && isToday && (
            <span className="ml-auto text-[9px] text-primary/80 uppercase tracking-wider font-semibold">today</span>
          )}

          {/* Promote-to-focus button: surfaces on hover, only for non-focus columns.
              Lives at the right edge so it doesn't fight the day label. */}
          {!isFocus && onPromote && (
            <Tooltip label={`Feature ${DAY_LABELS[day]} above`}>
            <IconButton
              onClick={onPromote}
              label={`Feature ${DAY_LABELS[day]} above`}
              size="sm"
              className={`${isToday ? 'ml-2' : 'ml-auto'} opacity-0 group-hover:opacity-100 focus-visible:opacity-100`}
            >
              <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M8 13V3M3 8l5-5 5 5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </IconButton>
            </Tooltip>
          )}
        </div>

        {pilots.length > 0 && (
          <div className={`flex flex-wrap gap-1 ${isFocus ? 'mt-3' : 'mt-1.5'}`}>
            {(() => {
              // Group pilots by event so each show renders as one chip listing
              // every assigned pilot. Preserves first-encountered order for
              // both events and pilots within an event.
              const byEvent = new Map<string, { name: string; names: string[] }>();
              for (const p of pilots) {
                const bucket = byEvent.get(p.event_id) ?? { name: p.event_name, names: [] };
                if (!bucket.names.includes(p.firstname)) bucket.names.push(p.firstname);
                byEvent.set(p.event_id, bucket);
              }
              return Array.from(byEvent.entries()).map(([eventId, { name, names }]) => (
                <PilotBadge
                  key={eventId}
                  eventId={eventId}
                  eventName={name}
                  pilotFirstNames={names}
                />
              ));
            })()}
          </div>
        )}
      </div>

      {/* Items list — overflow-y-auto so heavy days scroll inside the card
          rather than push the row taller than its sibling. */}
      <div ref={setNodeRef} className={`flex-1 min-h-0 overflow-y-auto ${sizeClasses.listPadding}`}>
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          {items.map(item => (
            <POAItem
              key={item.id}
              item={item}
              isManager={isManager}
              truncated={truncated}
              onUpdate={onUpdate}
              onComplete={onComplete}
              onDelete={onDelete}
            />
          ))}
        </SortableContext>

        <Reveal show={items.length === 0 && !composing}>
          <EmptyState variant="line" dense={!isFocus} className={`select-none justify-center text-center ${isFocus ? 'py-8' : 'py-4'}`}>
            {isManager ? 'Drop items here or add below' : 'No activities'}
          </EmptyState>
        </Reveal>

        <Reveal show={composing}>
          <div className="bg-gray-900/40 border border-gray-700/60 rounded-md p-2 space-y-1.5">
            <MentionInput
              value={draft}
              onChange={setDraft}
              onSubmit={submitCompose}
              placeholder="What's happening this day? Use @ to tag people."
              autoFocus
              rows={isFocus ? 3 : 2}
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={submitCompose}
                disabled={submitting || !draft.trim()}
                className={buttonClass('filled', 'sm')}
              >
                Add
              </button>
              <button
                type="button"
                onClick={cancelCompose}
                className={buttonClass('quiet', 'sm')}
              >
                Cancel
              </button>
              <label className="ml-auto flex items-center gap-1 text-[10px] text-on-surface-variant select-none">
                <input
                  type="checkbox"
                  checked={draftCompletable}
                  onChange={(e) => setDraftCompletable(e.target.checked)}
                  className="accent-primary"
                />
                Trackable
              </label>
            </div>
          </div>
        </Reveal>
      </div>

      <Appear show={isManager && !composing} as="div" className="self-start">
        <button
          type="button"
          onClick={startCompose}
          className={`${buttonClass('quiet', 'sm')} m-1.5 self-start`}
        >
          <svg width={isFocus ? '12' : '10'} height={isFocus ? '12' : '10'} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M8 3v10M3 8h10" strokeLinecap="round" />
          </svg>
          Add item
        </button>
      </Appear>
    </div>
  );
};

export default DayColumn;
export { dateForDay };
